import { isTauri, openExternalUrl, tauriFetch } from './tauriFetch';
import { config } from '../config';
import { logger } from './loggingService';

const OAUTH_REDIRECT_URI = 'conceptcanvas://oauth/callback';
const GOOGLE_AUTH_URL = 'https://accounts.google.com/o/oauth2/v2/auth';
const GOOGLE_TOKEN_URL = 'https://oauth2.googleapis.com/token';

interface OAuthState {
  state: string;
  codeVerifier: string;
  resolve: (tokens: OAuthTokens) => void;
  reject: (error: Error) => void;
}

interface OAuthTokens {
  access_token: string;
  refresh_token?: string;
  expires_in: number;
  token_type: string;
  scope: string;
}

let pendingOAuthRequest: OAuthState | null = null;
let deepLinkListenerInitialized = false;

function generateRandomString(length: number): string {
  const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789-._~';
  let result = '';
  const randomValues = new Uint8Array(length);
  crypto.getRandomValues(randomValues);
  for (let i = 0; i < length; i++) {
    result += chars[randomValues[i] % chars.length];
  }
  return result;
}

async function generateCodeChallenge(verifier: string): Promise<string> {
  const encoder = new TextEncoder();
  const data = encoder.encode(verifier);
  const digest = await crypto.subtle.digest('SHA-256', data);
  const base64 = btoa(String.fromCharCode(...new Uint8Array(digest)));
  return base64.replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

async function initializeDeepLinkListener(): Promise<void> {
  if (deepLinkListenerInitialized || !isTauri()) return;
  
  try {
    const { onOpenUrl, getCurrent } = await import('@tauri-apps/plugin-deep-link');
    const { listen } = await import('@tauri-apps/api/event');
    
    const handleDeepLink = (url: string) => {
      logger.info('DesktopAuth', 'Deep link received', { url: url.substring(0, 50) + '...' });
      
      if (url.startsWith('conceptcanvas://oauth/callback')) {
        handleOAuthCallback(url);
      }
    };
    
    const currentUrls = await getCurrent();
    if (currentUrls && currentUrls.length > 0) {
      for (const url of currentUrls) {
        handleDeepLink(url);
      }
    }
    
    await onOpenUrl((urls) => {
      for (const url of urls) {
        handleDeepLink(url);
      }
    });
    
    await listen('deep-link-received', (event) => {
      const url = event.payload as string;
      handleDeepLink(url);
    });
    
    deepLinkListenerInitialized = true;
    logger.info('DesktopAuth', 'Deep link listener initialized');
  } catch (error) {
    logger.error('DesktopAuth', 'Failed to initialize deep link listener', error);
  }
}

function handleOAuthCallback(url: string): void {
  if (!pendingOAuthRequest) {
    logger.warn('DesktopAuth', 'Received OAuth callback but no pending request');
    return;
  }
  
  try {
    const urlObj = new URL(url);
    const code = urlObj.searchParams.get('code');
    const state = urlObj.searchParams.get('state');
    const error = urlObj.searchParams.get('error');
    
    if (error) {
      const errorDesc = urlObj.searchParams.get('error_description') || error;
      pendingOAuthRequest.reject(new Error(`OAuth error: ${errorDesc}`));
      pendingOAuthRequest = null;
      return;
    }
    
    if (!code) {
      pendingOAuthRequest.reject(new Error('No authorization code received'));
      pendingOAuthRequest = null;
      return;
    }
    
    if (state !== pendingOAuthRequest.state) {
      pendingOAuthRequest.reject(new Error('OAuth state mismatch - possible CSRF attack'));
      pendingOAuthRequest = null;
      return;
    }
    
    logger.info('DesktopAuth', 'Authorization code received, exchanging for tokens');
    
    exchangeCodeForTokens(code, pendingOAuthRequest.codeVerifier)
      .then((tokens) => {
        pendingOAuthRequest?.resolve(tokens);
        pendingOAuthRequest = null;
      })
      .catch((error) => {
        pendingOAuthRequest?.reject(error);
        pendingOAuthRequest = null;
      });
      
  } catch (error: any) {
    logger.error('DesktopAuth', 'Error parsing OAuth callback URL', error);
    pendingOAuthRequest.reject(new Error(`Failed to parse callback: ${error.message}`));
    pendingOAuthRequest = null;
  }
}

async function exchangeCodeForTokens(code: string, codeVerifier: string): Promise<OAuthTokens> {
  const params = new URLSearchParams({
    code,
    client_id: config.google.clientId,
    redirect_uri: OAUTH_REDIRECT_URI,
    grant_type: 'authorization_code',
    code_verifier: codeVerifier,
  });
  
  const response = await tauriFetch(GOOGLE_TOKEN_URL, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/x-www-form-urlencoded',
    },
    body: params.toString(),
  });
  
  if (!response.ok) {
    const errorText = await response.text();
    logger.error('DesktopAuth', 'Token exchange failed', { status: response.status, error: errorText });
    throw new Error(`Token exchange failed: ${response.status}`);
  }
  
  const tokens = await response.json();
  logger.info('DesktopAuth', 'Token exchange successful');
  return tokens;
}

export const desktopAuthService = {
  async initialize(): Promise<void> {
    if (isTauri()) {
      await initializeDeepLinkListener();
    }
  },
  
  async signInWithGoogle(): Promise<OAuthTokens> {
    if (!isTauri()) {
      throw new Error('Desktop auth is only available in the desktop app');
    }
    
    if (!config.google.clientId) {
      throw new Error('Google Client ID not configured');
    }
    
    await initializeDeepLinkListener();
    
    const state = generateRandomString(32);
    const codeVerifier = generateRandomString(64);
    const codeChallenge = await generateCodeChallenge(codeVerifier);
    
    const params = new URLSearchParams({
      client_id: config.google.clientId,
      redirect_uri: OAUTH_REDIRECT_URI,
      response_type: 'code',
      scope: config.google.scopes,
      state,
      code_challenge: codeChallenge,
      code_challenge_method: 'S256',
      access_type: 'offline',
      prompt: 'consent',
    });
    
    const authUrl = `${GOOGLE_AUTH_URL}?${params.toString()}`;
    
    logger.info('DesktopAuth', 'Starting OAuth flow', { state });
    
    return new Promise<OAuthTokens>((resolve, reject) => {
      pendingOAuthRequest = { state, codeVerifier, resolve, reject };
      
      const timeout = setTimeout(() => {
        if (pendingOAuthRequest?.state === state) {
          pendingOAuthRequest.reject(new Error('OAuth timeout - please try again'));
          pendingOAuthRequest = null;
        }
      }, 300000);
      
      const originalResolve = resolve;
      const originalReject = reject;
      
      pendingOAuthRequest.resolve = (tokens) => {
        clearTimeout(timeout);
        originalResolve(tokens);
      };
      
      pendingOAuthRequest.reject = (error) => {
        clearTimeout(timeout);
        originalReject(error);
      };
      
      openExternalUrl(authUrl).catch((error) => {
        logger.error('DesktopAuth', 'Failed to open browser', error);
        pendingOAuthRequest?.reject(new Error('Failed to open browser for authentication'));
        pendingOAuthRequest = null;
      });
    });
  },
  
  async refreshAccessToken(refreshToken: string): Promise<OAuthTokens> {
    const params = new URLSearchParams({
      refresh_token: refreshToken,
      client_id: config.google.clientId,
      grant_type: 'refresh_token',
    });
    
    const response = await tauriFetch(GOOGLE_TOKEN_URL, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
      },
      body: params.toString(),
    });
    
    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Token refresh failed: ${response.status} - ${errorText}`);
    }
    
    return response.json();
  },
  
  isAvailable(): boolean {
    return isTauri() && !!config.google.clientId;
  },
};
